import { leafName } from "@/dnd";
import type { DraggableData } from "@/dnd/ids";
import {
  buildCopyPayloadFromRecord,
  type ExplorerCopySourceScope,
} from "@/dnd/payloads";
import {
  FilesRailFileRow,
  type FilesRailFileMenuActions,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import type { RailSelectionSource } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import type { FileRecord } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import { useMemo } from "react";

/** Draggable context file row — canvas / flow-session scope, Files-rail styling. */
export function FilesRailContextDraggableFileRow({
  record,
  scope,
  dragNamespace,
  active,
  section = "context",
  listItem = true,
  onOpen,
  sourcePlaced,
  sourceParentSectionId,
  menuActions,
  withChevronSpacer,
}: {
  record: FileRecord;
  scope: ExplorerCopySourceScope;
  dragNamespace: string;
  active?: boolean;
  section?: RailSelectionSource;
  /**
   * When false, the parent list host owns contiguous-selection list-item attrs
   * (see FilesRailContextFileRow).
   */
  listItem?: boolean;
  onOpen: () => void;
  menuActions?: FilesRailFileMenuActions;
  sourcePlaced?: boolean;
  sourceParentSectionId?: string | null;
  /** See FilesRailFileRow — align with expandable siblings' icon column. */
  withChevronSpacer?: boolean;
}) {
  const draggableData: DraggableData = useMemo(() => {
    const opts: { placed?: boolean; parentSectionId?: string | null } = {};
    if (sourcePlaced !== undefined) opts.placed = sourcePlaced;
    if (sourceParentSectionId !== undefined) {
      opts.parentSectionId = sourceParentSectionId;
    }
    return {
      kind: "files",
      payloads: [
        buildCopyPayloadFromRecord(
          record,
          scope,
          Object.keys(opts).length > 0 ? opts : undefined,
        ),
      ],
      primaryName: leafName(record.path),
    };
  }, [record, scope, sourcePlaced, sourceParentSectionId]);

  const draggable = useDraggable({
    id: `file-row:${dragNamespace}:${record.id}`,
    data: draggableData,
  });

  return (
    <FilesRailFileRow
      record={record}
      active={active}
      section={section}
      listItem={listItem}
      onOpen={onOpen}
      menuActions={menuActions}
      withChevronSpacer={withChevronSpacer}
      dragRef={draggable.setNodeRef}
      dragProps={{
        ...draggable.attributes,
        ...draggable.listeners,
      }}
      isDragging={draggable.isDragging}
    />
  );
}
